import React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Box,
  Heading,
  Text,
  Skeleton,
  useColorModeValue,
  Button,
  Tooltip,
  Icon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";

import { AllRecordWithUser } from "../types";
import { formatDate, parseDowntime } from "../utils/helpers";
import CollapsibleList from "./CollapsibleList";

interface DataTableProps {
  data: AllRecordWithUser[];
  isLoading: boolean;
  error: unknown;
  showUserId?: boolean;
  recordType?: "Data" | "Alpha";
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  isLoading,
  error,
  showUserId = false,
  recordType = "Data",
}) => {
  const navigate = useNavigate();
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  const handleRowClick = (id: number) => {
    navigate(`/record/data/${id}`);
  };

  const CollapsibleBadges = ({
    items,
    colorScheme,
    maxItems = 5,
  }: {
    items: string[];
    colorScheme: string;
    maxItems?: number;
  }) => {
    if (!items || items.length === 0) return <Text>-</Text>;

    const showItems = items.slice(0, maxItems);
    const remaining = items.length - maxItems;

    return (
      <Box display="flex" gap={1} flexWrap="wrap">
        {showItems.map((item) => (
          <Badge key={item} colorScheme={colorScheme}>
            {item}
          </Badge>
        ))}
        {remaining > 0 && (
          <Tooltip label={items.slice(maxItems).join(", ")}>
            <Badge variant="solid" cursor="pointer" colorScheme="gray">
              +{remaining}
            </Badge>
          </Tooltip>
        )}
      </Box>
    );
  };

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="lg" color="red.500">
          Error Loading Data
        </Heading>
        <Text mt={4} color="gray.500">
          {error instanceof Error
            ? error.message
            : "An unexpected error occurred"}
        </Text>
        <Button
          mt={6}
          colorScheme="blue"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer borderRadius="md" boxShadow="sm" bg="white">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Active</Th>
              <Th>ID</Th>
              <Th>Name</Th>
              <Th minW="140px">Symbol</Th>
              <Th>Features</Th>
              <Th>Encryption</Th>
              <Th>Last Updated</Th>
              <Th>Created At</Th>
              <Th>Live Start Time</Th>
              <Th>Update Frequency</Th>
              <Th maxW="260px">Downtime%</Th>
              {showUserId && <Th >Provider</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Tr key={index}>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  {showUserId && (
                    <Td>
                      <Skeleton height="20px" />
                    </Td>
                  )}
                </Tr>
              ))
            ) : data.length > 0 ? (
              data.map((recordWithUser) => {
                const record = recordWithUser.record;
                const user = recordWithUser.user;
                const active = Boolean(record.is_active);
                const symbols = record.symbols || [];
                // todo: 暂时写死
                const downtimeValue =  parseDowntime(record.downtime, "all")
                // const downtimeValue = active
                //   ? parseDowntime(record.downtime, "all")
                //   : "-";

                const provider = user?.contact || user?.name;
                return (
                  <Tr
                    key={`${record.id}-${symbols[0] || "record"}`}
                    _hover={{ bg: hoverBg, cursor: "pointer" }}
                    onClick={() => handleRowClick(record.id)}
                  >
                    <Td>
                      {active ? (
                        <Icon as={FaCheck} color="green.500" boxSize={4} />
                      ) : (
                        <Icon as={FaTimes} color="red.500" boxSize={4} />
                      )}
                    </Td>
                    <Td>{record.id}</Td>
                    <Td fontWeight="medium">{record.name}</Td>
                    <Td>
                      <CollapsibleBadges
                        items={symbols}
                        colorScheme="blue"
                        maxItems={3}
                      />
                    </Td>
                    <Td>
                      <CollapsibleList
                        items={record.features?.map((f) => f.name) || []}
                      />
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={record.is_encrypted ? 'yellow' : 'gray'}
                        color={record.is_encrypted ? 'yellow.800' : 'gray.700'}
                      >
                        {record.is_encrypted ? 'ENCRYPTED' : 'Unencrypted'}
                      </Badge>
                    </Td>
                    <Td>
                      {formatDate(record.last_updated, { precision: "seconds" })}
                    </Td>
                    <Td>
                      {formatDate(record.created_at, { precision: "seconds" })}
                    </Td>
                    <Td>
                      {record.live_start_time
                        ? formatDate(record.live_start_time, {
                            precision: "seconds",
                          })
                        : "null"}
                    </Td>
                    <Td>{record.update_frequency} ms</Td>

                    <Td maxW="260px" whiteSpace="normal" wordBreak="break-word">
                      {downtimeValue}
                    </Td>
                    {showUserId && (
                      <Td whiteSpace="normal" wordBreak="break-word">
                        {provider ? (
                          <Tooltip label={user?.name || "Unknown"}>
                            <Text>{provider}</Text>
                          </Tooltip>
                        ) : (
                          <Text>Anonymous</Text>
                        )}
                      </Td>
                    )}
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td colSpan={showUserId ? 12 : 11} textAlign="center" py={6}>
                  <Text color="gray.500">
                    No {recordType.toLowerCase()} records found
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataTable;
